import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { PricingSection } from "../components/pricing/PricingSection";
import { MarketingLayout } from "../components/marketing/MarketingLayout";
import { Reveal, useInView } from "../components/marketing/hooks";
import { usePlatformStats } from "../components/marketing/usePlatformStats";
import { PlatformShowcase } from "../components/marketing/sections/PlatformShowcase";
import { FeaturesSection } from "../components/marketing/sections/FeaturesSection";
import { UCATSectionsOverview } from "../components/marketing/sections/UCATSectionsOverview";
import { ComparisonSection } from "../components/marketing/sections/ComparisonSection";
import { TestimonialsSection } from "../components/marketing/sections/TestimonialsSection";
import { FAQSection } from "../components/marketing/sections/FAQSection";
import { Marquee } from "../components/marketing/sections/Marquee";
import { FinalCTASection } from "../components/marketing/sections/FinalCTASection";
import { StatsSection } from "../components/marketing/sections/StatsSection";
import { ArrowRight, CheckCircle2, TrendingUp } from "lucide-react";
import { Seo, faqPageSchema, organizationSchema, softwareApplicationSchema, webPageSchema, websiteSchema } from "../components/seo/Seo";
import { FAQS } from "../components/marketing/content";

function AnimatedHeading({ line1, line2 }) {
  const [ref, inView] = useInView(0.3);
  let charIdx = 0;

  const renderLine = (line, isLine2) =>
    line.split(" ").map((word, wordIdx, words) => (
      <React.Fragment key={`w-${isLine2 ? 2 : 1}-${wordIdx}`}>
        {/* Keep each word on one line while still animating per character */}
        <span style={{ display: "inline-block", whiteSpace: "nowrap" }}>
          {word.split("").map((ch, i) => {
            const idx = charIdx++;
            return (
              <span
                key={`c-${i}`}
                className={isLine2 ? "text-primary" : ""}
                style={{
                  display: "inline-block",
                  opacity: inView ? 1 : 0,
                  transform: inView ? "none" : "translateY(24px)",
                  transition: `opacity 0.4s ease ${idx * 0.025}s, transform 0.4s ease ${idx * 0.025}s`,
                }}
              >
                {ch}
              </span>
            );
          })}
        </span>
        {wordIdx < words.length - 1 ? " " : null}
      </React.Fragment>
    ));

  return (
    <h1
      ref={ref}
      className="text-5xl sm:text-6xl lg:text-7xl font-cabinet font-black leading-[1.08] tracking-tight text-center"
    >
      {renderLine(line1, false)}
      <br />
      {renderLine(line2, true)}
    </h1>
  );
}

function HeroBackground() {
  return (
    <div
      className="absolute inset-0 overflow-hidden pointer-events-none"
      aria-hidden="true"
      style={{ zIndex: 0 }}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% 40%, transparent 0%, hsl(220 40% 96%) 100%)",
        }}
      />
      <svg
        className="absolute inset-0 w-full h-full"
        style={{
          opacity: 0,
          animation: "doodle-fade 1.5s ease-out 0.3s forwards",
        }}
      >
        <defs>
          <pattern
            id="dot-grid"
            x="0"
            y="0"
            width="32"
            height="32"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="16" cy="16" r="1" fill="#0055FF" opacity="0.18" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dot-grid)" />
      </svg>
    </div>
  );
}

export const LandingPage = () => {
  const platformStats = usePlatformStats();

  return (
    <>
      <Seo
        title="UCAT ANZ Practice Platform with Percentile Analytics | Med900"
        description="Prepare for UCAT ANZ with exam-accurate mocks, percentile tracking, timed practice, section analytics, and data-driven study guides."
        path="/"
        keywords="UCAT ANZ practice, UCAT practice questions, UCAT mocks, UCAT percentile analytics, UCAT study guides, Med900"
        jsonLd={[
          organizationSchema(),
          websiteSchema(),
          softwareApplicationSchema(),
          webPageSchema({
            path: "/",
            name: "Med900 UCAT ANZ Practice Platform",
            description:
              "A data-driven UCAT ANZ preparation platform with exam-accurate mocks, analytics, guides, and targeted practice.",
          }),
          faqPageSchema(FAQS),
        ]}
      />
      <MarketingLayout>
      <section
        className="pt-8 pb-24 px-4 relative overflow-hidden"
        data-testid="hero-section"
        style={{
          background:
            "linear-gradient(180deg, hsl(220 50% 96%) 0%, hsl(220 30% 98%) 50%, hsl(0 0% 100%) 100%)",
        }}
      >
        <HeroBackground />

        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1100px] h-[700px] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, hsla(221, 100%, 50%, 0.15) 0%, transparent 70%)",
          }}
        />

        <div className="max-w-3xl mx-auto text-center relative z-10">
          <Reveal delay={0.1}>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm text-foreground rounded-full text-sm font-medium border border-border/50 font-plex mb-8"
              data-testid="social-proof"
            >
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Built for data-driven UCAT prep &mdash; every session scored
            </div>
          </Reveal>

          <AnimatedHeading
            line1="UCAT ANZ practice with"
            line2="percentile analytics."
          />

          <Reveal delay={0.5}>
            <p className="mt-6 text-xl text-foreground/70 max-w-lg mx-auto leading-snug font-plex font-medium">
              Track your percentile. Spot your weaknesses. Study what{" "}
              <strong className="text-foreground">actually matters</strong>.
            </p>
          </Reveal>

          <Reveal delay={0.6}>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/signup">
                <Button
                  size="lg"
                  className="text-base font-semibold font-cabinet rounded-full px-8 group"
                  data-testid="hero-cta"
                >
                  Get Started{" "}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/platform">
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base font-plex rounded-full px-8"
                >
                  Explore features
                </Button>
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.7}>
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-sm text-muted-foreground font-plex mt-8">
              {[
                "Percentile analytics",
                "Score trajectory tracking",
                "Exam-accurate interface",
                "Evidence-based study plans",
              ].map((t) => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />{" "}
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.4}>
          <div
            className="max-w-2xl mx-auto mt-16 relative z-10"
            data-testid="hero-visual"
          >
            <div className="rounded-sm border-2 border-[#336699] shadow-2xl overflow-hidden bg-white font-exam exam-no-select">
              <div className="bg-[#336699] h-8 flex items-center justify-between px-4">
                <span className="text-white text-sm font-medium">
                  Verbal Reasoning
                </span>
                <div className="flex items-center gap-4 text-white text-sm">
                  <span>20:42</span>
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 6h16M4 10h16M4 14h16M4 18h16"
                      />
                    </svg>
                    14 of 44
                  </span>
                </div>
              </div>

              <div className="p-6 bg-white">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/78e9c625-56cd-4913-9223-efda51a82847/images/6a1c48263f9fac63001677dc4f29f0a105f70812f6a1e8f25eab74e6d76efd95.png"
                  alt="Student studying with Med900 analytics"
                  className="w-full h-56 object-cover rounded-sm border border-gray-200"
                />
                <div className="mt-4 space-y-3 text-sm text-black">
                  {["True", "False", "Can't Tell"].map((opt, i) => (
                    <div key={opt} className="flex items-start gap-3">
                      <div
                        className={`flex-shrink-0 w-4 h-4 mt-0.5 rounded-full border-2 flex items-center justify-center ${
                          i === 0
                            ? "border-[#336699] bg-[#336699]"
                            : "border-gray-500 bg-white"
                        }`}
                      >
                        {i === 0 && (
                          <div className="w-1.5 h-1.5 rounded-full bg-white" />
                        )}
                      </div>
                      <span>{opt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div
              className="absolute -bottom-5 right-8 bg-white border border-border/50 rounded-2xl p-3 shadow-lg flex items-center gap-2.5"
              style={{ animation: "badge-pulse 3s ease-in-out infinite" }}
            >
              <div className="w-8 h-8 bg-success/10 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-success" />
              </div>
              <div>
                <div className="text-[10px] text-muted-foreground font-medium font-plex">
                  Avg. improvement
                </div>
                <div className="text-sm font-black text-success font-mono">
                  +287 pts
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <div className="max-w-5xl mx-auto mt-24 relative z-10">
          <StatsSection stats={platformStats} />
        </div>
      </section>

      <PlatformShowcase />
      <FeaturesSection />
      <UCATSectionsOverview />
      <ComparisonSection stats={platformStats} />
      <TestimonialsSection />
      <PricingSection RevealWrapper={Reveal} />
      <FAQSection />
      <Marquee text="Data-driven UCAT preparation" speed={35} />
      <FinalCTASection platformStats={platformStats} />
      </MarketingLayout>
    </>
  );
};
